import React from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicyPage() {
  return (
    <div className="page">
      <div className="container">
        <div className="content-page">
          <h1>Privacy Policy</h1>
          
          <section className="content-section">
            <p className="lead">
              <strong>Last Updated:</strong> January 7, 2026
            </p>
            <p className="lead">
              The Queer Data Network (QDN) is committed to protecting your privacy and ensuring 
              the safety of our community members. This Privacy Policy explains how we collect, 
              use, and protect your personal information.
            </p>
          </section>

          <section className="content-section">
            <h2>Data Collection and Usage</h2>
            
            <h3>Anonymous Usage Analytics</h3>
            <p>
              We collect anonymous usage data to improve the QDN platform and understand how our 
              community uses our resources. This helps us:
            </p>
            <ul>
              <li>Identify popular content and topics</li>
              <li>Improve site navigation and user experience</li>
              <li>Understand community engagement patterns</li>
              <li>Make data-driven decisions about content and features</li>
            </ul>

            <h3>What We Track</h3>
            <p>When you view content on QDN, we automatically collect:</p>
            <ul>
              <li>Which pages and resources you view</li>
              <li>How long you spend on each page</li>
              <li>What type of content you engage with (articles, events, discussions)</li>
              <li>General navigation patterns (referrer information)</li>
              <li>Timestamp of your visit</li>
            </ul>
            <p>For authenticated users, we additionally associate:</p>
            <ul>
              <li>Your user ID (to understand member behavior)</li>
              <li>Your role (member, moderator, admin)</li>
            </ul>

            <h3>What We DON'T Track</h3>
            <p>We do NOT collect:</p>
            <ul>
              <li>Your name, email, or personal contact information in pageviews</li>
              <li>Your IP address or precise location in analytics data</li>
              <li>Your browsing history outside of QDN</li>
              <li>Any personally identifiable information beyond your user account</li>
            </ul>

            <h3>Session Tracking</h3>
            <p>
              We use session identifiers stored locally in your browser (sessionStorage) to prevent 
              duplicate view counting within a 5-minute window. These session IDs:
            </p>
            <ul>
              <li>Are randomly generated</li>
              <li>Expire when you close your browser</li>
              <li>Are not cookies and do not persist across sessions</li>
              <li>Cannot be used to identify you personally</li>
            </ul>

            <h3>Analytics Data Retention</h3>
            <p>
              Pageview data is automatically deleted after <strong>90 days</strong>. We do not retain 
              historical analytics data beyond this period.
            </p>

            <h3>Third-Party Sharing</h3>
            <p>We DO NOT:</p>
            <ul>
              <li>Sell your data to third parties</li>
              <li>Share your usage data with advertisers</li>
              <li>Use third-party tracking scripts or analytics (e.g., Google Analytics)</li>
              <li>Allow external parties to access your behavioral data</li>
            </ul>
            <p>
              All analytics are internal and used solely to improve the QDN platform for our community.
            </p>

            <h3>Analytics Rights</h3>
            <p>If you are an authenticated user, you have the right to:</p>
            <ul>
              <li>Request deletion of your pageview data (contact administrators)</li>
              <li>Opt out of analytics tracking (use private/incognito browsing)</li>
              <li>Access information about what analytics data we've collected about you</li>
            </ul>
            <p>
              Anonymous users can browse in private/incognito mode to prevent session tracking.
            </p>
          </section>

          <section className="content-section">
            <h2>Information We Collect</h2>
            
            <h3>Account Information</h3>
            <p>When you create an account, we collect:</p>
            <ul>
              <li><strong>Email address:</strong> Used for account authentication and important communications</li>
              <li><strong>Display name:</strong> Your chosen public-facing name (can be a pseudonym)</li>
              <li><strong>Password:</strong> Stored securely using bcrypt hashing (we never store plaintext passwords)</li>
            </ul>

            <h3>Profile Information (Optional)</h3>
            <p>You may choose to provide:</p>
            <ul>
              <li>Bio/About section</li>
              <li>Location (general area, not specific address)</li>
              <li>Links to your website or social media profiles</li>
              <li>Avatar/profile picture</li>
            </ul>

            <h3>Content & Activity</h3>
            <p>When you participate on QDN, we store:</p>
            <ul>
              <li>Resources you submit (datasets, research, tools, guides)</li>
              <li>Community posts (announcements, events, discussions)</li>
              <li>Comments and reactions</li>
              <li>Timestamps of your activity</li>
            </ul>

            <h3>Technical Information</h3>
            <p>We automatically collect:</p>
            <ul>
              <li>IP addresses for security and abuse prevention</li>
              <li>Browser type and device information</li>
              <li>Pages visited and time spent on the site</li>
              <li>Referral sources</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li><strong>Provide and improve our services:</strong> Enable account functionality, display your contributions, and enhance the platform</li>
              <li><strong>Communicate with you:</strong> Send account notifications, respond to inquiries, and share important updates</li>
              <li><strong>Ensure safety:</strong> Prevent abuse, enforce our Code of Conduct, and protect the community</li>
              <li><strong>Analyze usage:</strong> Understand how people use QDN to make improvements</li>
            </ul>
            <p>
              <strong>We will never:</strong>
            </p>
            <ul>
              <li>Sell your personal information to third parties</li>
              <li>Share your email address publicly</li>
              <li>Use your data for advertising purposes</li>
              <li>Track you across other websites</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Information Sharing & Disclosure</h2>
            
            <h3>Public Information</h3>
            <p>The following information is publicly visible to all users:</p>
            <ul>
              <li>Your display name</li>
              <li>Your profile information (bio, location, links, avatar)</li>
              <li>Resources, posts, comments, and reactions you create</li>
              <li>Your contribution history</li>
            </ul>

            <h3>Private Information</h3>
            <p>The following information is never shared publicly:</p>
            <ul>
              <li>Your email address</li>
              <li>Your password (stored encrypted)</li>
              <li>Your IP address</li>
              <li>Any information you don't explicitly choose to share</li>
            </ul>

            <h3>Legal Requirements</h3>
            <p>
              We may disclose your information if required by law, such as in response to a 
              valid court order or subpoena. We will notify you of such requests unless legally 
              prohibited from doing so.
            </p>
          </section>

          <section className="content-section">
            <h2>Data Security</h2>
            <p>We take security seriously and implement measures to protect your data:</p>
            <ul>
              <li><strong>Encryption:</strong> Passwords are hashed using bcrypt; data transmission uses HTTPS</li>
              <li><strong>Access controls:</strong> Limited access to user data for authorized administrators only</li>
              <li><strong>Regular updates:</strong> We keep our systems and dependencies up to date with security patches</li>
              <li><strong>Monitoring:</strong> We monitor for suspicious activity and unauthorized access</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the 
              internet is 100% secure. We encourage you to use strong passwords and not share 
              sensitive personal information publicly.
            </p>
          </section>

          <section className="content-section">
            <h2>Your Rights & Choices</h2>
            
            <h3>Access & Control</h3>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access your data:</strong> View all information we have about you</li>
              <li><strong>Update your information:</strong> Edit your profile and account details at any time</li>
              <li><strong>Delete your content:</strong> Remove posts, comments, and resources you've created</li>
              <li><strong>Delete your account:</strong> Request permanent deletion of your account and associated data</li>
            </ul>

            <h3>Data Portability</h3>
            <p>
              You can request a copy of your data in a portable format. Contact us at 
              <strong> privacy@queerdatanetwork.com</strong> to make this request.
            </p>

            <h3>Opting Out</h3>
            <p>You can opt out of:</p>
            <ul>
              <li>Non-essential email notifications (in your account settings)</li>
              <li>Public profile visibility (by making your profile private)</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Cookies & Tracking</h2>
            <p>QDN uses minimal cookies and storage for essential functionality:</p>
            <ul>
              <li><strong>Authentication cookies:</strong> Keep you logged in between sessions</li>
              <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Session Storage:</strong> Temporary session identifiers (not cookies) to prevent duplicate pageview counting</li>
            </ul>

            <h3>Session Storage vs Cookies</h3>
            <p>
              QDN uses browser sessionStorage (not cookies) for temporary session identification during 
              your visit. Unlike cookies:
            </p>
            <ul>
              <li>SessionStorage data never leaves your browser</li>
              <li>It is automatically deleted when you close the browser</li>
              <li>It cannot track you across websites</li>
              <li>It does not require cookie consent banners</li>
            </ul>
            <p>
              We use sessionStorage solely to prevent duplicate pageview counting during your current 
              browsing session.
            </p>

            <p>
              We do not use third-party tracking cookies or advertising cookies. We do not 
              participate in cross-site tracking or sell data to advertisers.
            </p>
          </section>

          <section className="content-section">
            <h2>Data Retention</h2>
            <p>We retain your data as follows:</p>
            <ul>
              <li><strong>Account data:</strong> Retained as long as your account is active</li>
              <li><strong>Content:</strong> Retained indefinitely unless you delete it</li>
              <li><strong>Deleted accounts:</strong> Permanently removed within 30 days of deletion request</li>
              <li><strong>Logs:</strong> Technical logs are retained for up to 90 days for security purposes</li>
            </ul>
            <p>
              Note: Some content may remain visible in archives or backups for a limited time 
              after deletion, but will not be publicly accessible.
            </p>
          </section>

          <section className="content-section">
            <h2>Third-Party Services</h2>
            <p>QDN uses the following third-party services:</p>
            <ul>
              <li><strong>Azure (Microsoft):</strong> Hosting and database services</li>
              <li><strong>Stripe (planned):</strong> Payment processing for donations (your payment info is never stored on our servers)</li>
            </ul>
            <p>
              These services have their own privacy policies. We only share information necessary 
              for them to provide their services, and we carefully vet all third-party providers.
            </p>
          </section>

          <section className="content-section">
            <h2>Children's Privacy</h2>
            <p>
              QDN is not intended for users under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we discover that a child under 13 
              has provided us with personal information, we will delete it immediately.
            </p>
          </section>

          <section className="content-section">
            <h2>International Users</h2>
            <p>
              QDN is operated in the United States. If you are accessing QDN from outside the 
              U.S., please be aware that your information may be transferred to, stored, and 
              processed in the U.S., where data protection laws may differ from those in your 
              country.
            </p>
          </section>

          <section className="content-section">
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make significant 
              changes, we will notify you by email or through a prominent notice on the website. 
              Your continued use of QDN after changes take effect constitutes acceptance of the 
              updated policy.
            </p>
          </section>

          <section className="content-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or 
              your personal data, please contact us:
            </p>
            <ul>
              <li><strong>Email:</strong> privacy@queerdatanetwork.com</li>
              <li><strong>General inquiries:</strong> <Link to="/contact">Contact form</Link></li>
            </ul>
          </section>

          <section className="content-section">
            <h2>LGBTQ+ Safety Note</h2>
            <p>
              We understand that privacy is especially important for LGBTQ+ individuals, who 
              may face unique safety concerns. That's why we:
            </p>
            <ul>
              <li>Allow the use of pseudonyms and do not require real names</li>
              <li>Never publicly display email addresses</li>
              <li>Give you full control over what information you share</li>
              <li>Take harassment and doxxing seriously (see our <Link to="/code-of-conduct">Code of Conduct</Link>)</li>
            </ul>
            <p>
              Your safety is our priority. If you have specific privacy or safety concerns, 
              please reach out to us at <strong>safety@queerdatanetwork.com</strong>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
